import React, { useContext, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { UserContext } from "../contexts";
import "./store-switcher.scss";

const ROLE_LABELS = {
  owner: "Owner",
  store_admin: "Store admin",
  sales_admin: "Sales admin",
};

const roleLabel = (role) => ROLE_LABELS[role] || role;

const ISwitch = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 7h13l-3-3" />
    <path d="M20 7l-3 3" />
    <path d="M20 17H7l3 3" />
    <path d="M4 17l3-3" />
  </svg>
);

const IChevron = ({ open }) => (
  <svg
    width="12" height="12" viewBox="0 0 12 12" fill="none"
    style={{ transition: "transform 200ms ease", transform: open ? "rotate(180deg)" : "rotate(0deg)", flexShrink: 0 }}
  >
    <path d="M2 4.5l4 4 4-4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/**
 * Store switcher — lets a user who belongs to more than one store (Phase 5
 * of the multi-store access rework) act as a different one. Hidden entirely
 * when there's nothing to switch between — most users belong to exactly one
 * store and must never see this at all.
 *
 * A custom trigger + portal-rendered menu, NOT a native <select>: a native
 * select's open list is a browser/OS popup that can't be restyled to match
 * the RC theme (it rendered with default light text on a system highlight
 * colour, nothing like the rest of this app), and the closed box's text was
 * genuinely clipped by the sidebar's own width — 200px expanded, 72px
 * collapsed (rc-admin.scss's hover-to-expand rail), nowhere near enough for
 * "Test Store B — Sales admin". The trigger reuses the sidebar's own
 * icon/label markup (rc-sidebar__icon / rc-sidebar__label) so it collapses
 * to an icon exactly like every other nav item instead of rendering a
 * half-cut string, and the open menu is portaled to document.body so it is
 * never constrained by the sidebar's own width or clipped by its
 * `overflow: hidden` — it can always show the full store name and role.
 *
 * Switching is server-validated on every request (store_user membership,
 * checked by App\Http\Middleware\ResolveActiveStore) — this UI can only
 * ever surface stores the backend already confirmed real access to
 * (GET /api/stores/accessible, fetched into UserContext).
 *
 * Lists REAL stores only. The old "Master Store — All Stores" entry (a
 * null-id sentinel for the unscoped master view) is gone: nothing can be
 * created or saved from there (useStoreWriteGuard blocks every create,
 * Store Settings has no team to show, half the sidebar hides), so it was an
 * option whose only effect was to break the next thing the user tried. A
 * master now starts on their default store and switches between real ones.
 */
const StoreSwitcher = () => {
  const { user, activeStoreId, accessibleStores, setActiveStore } =
    useContext(UserContext);
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  // "Master Store (All Stores)" is no longer offered — the backend stopped
  // returning its null-id sentinel (StoreController@accessible, 2026-08-25),
  // because nothing can actually be created or saved from the unscoped view.
  // Still filtered here rather than assumed away: frontend and backend deploy
  // independently, so this build must not render a stale API's sentinel as a
  // nameless, unselectable row.
  const stores = (accessibleStores || []).filter((s) => s && s.id != null);

  const currentId =
    activeStoreId ?? stores.find((s) => s.is_default)?.id ?? null;
  const currentStore = stores.find((s) => s.id === currentId) ?? null;

  useEffect(() => {
    if (!open) return undefined;

    // Flyout to the right of the rail, bottom-aligned with the trigger — works
    // identically whether the sidebar is collapsed (72px) or expanded (200px),
    // and never has to fit store names inside either width.
    const positionMenu = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;
      setMenuStyle({
        left: rect.right + 8,
        bottom: window.innerHeight - rect.bottom,
        minWidth: 220,
      });
    };

    positionMenu();

    const onOutsideClick = (e) => {
      if (triggerRef.current?.contains(e.target)) return;
      if (menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", onOutsideClick);
    document.addEventListener("keydown", onKeyDown);
    window.addEventListener("resize", positionMenu);
    window.addEventListener("scroll", positionMenu, true);

    return () => {
      document.removeEventListener("mousedown", onOutsideClick);
      document.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("resize", positionMenu);
      window.removeEventListener("scroll", positionMenu, true);
    };
  }, [open]);

  if (!user || stores.length < 2) {
    return null;
  }

  const selectStore = (storeId) => {
    setOpen(false);
    if (storeId !== currentId) {
      setActiveStore(storeId);
    }
  };

  return (
    <>
      <button
        type="button"
        ref={triggerRef}
        className={`rc-sidebar__link store-switcher__trigger${open ? " store-switcher__trigger--open" : ""}`}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        // Explicit aria-label: the button's visible content is the CURRENT
        // store's name/role, which would otherwise become its accessible
        // name too ("Test Store A Owner") and give no indication it's a
        // switcher control.
        aria-label="Switch store"
        title={currentStore ? `Switch store (currently ${currentStore.name})` : "Switch store"}
      >
        <span className="rc-sidebar__icon">
          <ISwitch />
        </span>
        <span className="rc-sidebar__label store-switcher__label">
          <span className="store-switcher__store-name">
            {currentStore?.name || "Switch store"}
          </span>
          {currentStore?.role && (
            <span className="store-switcher__role">{roleLabel(currentStore.role)}</span>
          )}
        </span>
        <span className="store-switcher__chevron">
          <IChevron open={open} />
        </span>
      </button>

      {open && menuStyle &&
        createPortal(
          <div
            ref={menuRef}
            className="store-switcher__menu"
            role="listbox"
            aria-label="Switch store"
            style={{
              left: menuStyle.left,
              bottom: menuStyle.bottom,
              minWidth: menuStyle.minWidth,
            }}
          >
            {stores.map((store) => {
              const selected = store.id === currentId;

              return (
                <button
                  type="button"
                  key={store.id}
                  role="option"
                  aria-selected={selected}
                  className={`store-switcher__option${selected ? " store-switcher__option--active" : ""}`}
                  onClick={() => selectStore(store.id)}
                >
                  <span className="store-switcher__option-name">{store.name}</span>
                  {store.role && (
                    <span className="store-switcher__option-role">{roleLabel(store.role)}</span>
                  )}
                </button>
              );
            })}
          </div>,
          document.body
        )}
    </>
  );
};

export default StoreSwitcher;
